/**
 * In-memory mock state for the Settings page.
 * No backend — every value here seeds local React state inside SettingsPage
 * and its section components. Nothing here is persisted.
 */

/* ── Profile ─────────────────────────────────────────────────────────── */

export interface UserProfile {
  fullName: string;
  email: string;
  avatarInitials: string;
  avatarUrl: string | null;
}

export const initialUserProfile: UserProfile = {
  fullName: "Meherun Nahar",
  email: "meherun@auralocloset.com",
  avatarInitials: "MN",
  avatarUrl: null,
};

/* ── Business profile ────────────────────────────────────────────────── */

export type BusinessType =
  | "retail_fashion"
  | "electronics"
  | "food_beverage"
  | "services"
  | "other";

export const businessTypeOptions: { value: BusinessType; label: string }[] = [
  { value: "retail_fashion", label: "Retail & Fashion" },
  { value: "electronics", label: "Electronics" },
  { value: "food_beverage", label: "Food & Beverage" },
  { value: "services", label: "Services" },
  { value: "other", label: "Other" },
];

export interface TimezoneOption {
  value: string;
  label: string;
}

export const timezoneOptions: TimezoneOption[] = [
  { value: "Asia/Dhaka", label: "Asia/Dhaka (GMT+6)" },
  { value: "Asia/Kolkata", label: "Asia/Kolkata (GMT+5:30)" },
  { value: "Asia/Dubai", label: "Asia/Dubai (GMT+4)" },
  { value: "Europe/London", label: "Europe/London (GMT+0/+1)" },
  { value: "America/New_York", label: "America/New_York (GMT-5/-4)" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles (GMT-8/-7)" },
];

export type CurrencyCode = "USD" | "EUR" | "GBP" | "BDT" | "INR" | "AED";

export const currencyOptions: { value: CurrencyCode; label: string }[] = [
  { value: "USD", label: "USD — US Dollar" },
  { value: "EUR", label: "EUR — Euro" },
  { value: "GBP", label: "GBP — British Pound" },
  { value: "BDT", label: "BDT — Bangladeshi Taka" },
  { value: "INR", label: "INR — Indian Rupee" },
  { value: "AED", label: "AED — UAE Dirham" },
];

export interface BusinessProfile {
  businessName: string;
  businessType: BusinessType;
  description: string;
  logoInitials: string;
  logoUrl: string | null;
  timezone: string;
  currency: CurrencyCode;
  supportEmail: string;
}

export const initialBusinessProfile: BusinessProfile = {
  businessName: "Aura Closet",
  businessType: "retail_fashion",
  description:
    "Aura Closet sells women's ready-to-wear fashion and accessories online, with same-day delivery inside Dhaka and nationwide shipping across Bangladesh.",
  logoInitials: "AC",
  logoUrl: null,
  timezone: "Asia/Dhaka",
  currency: "BDT",
  supportEmail: "support@auralocloset.com",
};

/* ── Team & roles ────────────────────────────────────────────────────── */

export type TeamRole = "Owner" | "Admin" | "Agent";

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: TeamRole;
}

export const initialTeamMembers: TeamMember[] = [
  { id: "mem-1", name: "Meherun Nahar", email: "meherun@auralocloset.com", role: "Owner" },
  { id: "mem-2", name: "Tanvir Ahmed", email: "tanvir@auralocloset.com", role: "Admin" },
  { id: "mem-3", name: "Farzana Islam", email: "farzana@auralocloset.com", role: "Agent" },
  { id: "mem-4", name: "Rakib Hasan", email: "rakib@auralocloset.com", role: "Agent" },
];

export interface PendingInvite {
  id: string;
  email: string;
  role: TeamRole;
}

export const initialPendingInvites: PendingInvite[] = [
  { id: "inv-1", email: "nusrat@auralocloset.com", role: "Agent" },
];

export const inviteRoleOptions: TeamRole[] = ["Admin", "Agent"];

/* ── Billing & plan ──────────────────────────────────────────────────── */

export interface PlanDetails {
  name: string;
  priceLabel: string;
  billingCycle: string;
  features: string[];
}

export const currentPlan: PlanDetails = {
  name: "Growth",
  priceLabel: "$49/mo",
  billingCycle: "Billed monthly",
  features: [
    "3 connected channels (Messenger, Instagram, WhatsApp)",
    "1 storefront integration (Shopify or WooCommerce)",
    "Unlimited AI-handled conversations",
    "Team seats for up to 5 agents",
  ],
};

export interface UsageStats {
  messagesUsed: number;
  messageLimit: number;
}

export const currentUsage: UsageStats = {
  messagesUsed: 6420,
  messageLimit: 10000,
};

export interface PaymentMethod {
  brand: string;
  lastFour: string;
  expiry: string;
}

export const currentPaymentMethod: PaymentMethod = {
  brand: "Visa",
  lastFour: "4242",
  expiry: "09/27",
};

export type InvoiceStatus = "Paid" | "Due";

export interface Invoice {
  id: string;
  date: string;
  amount: string;
  status: InvoiceStatus;
}

export const invoiceHistory: Invoice[] = [
  { id: "inv-2026-06", date: "Jun 1, 2026", amount: "$49.00", status: "Paid" },
  { id: "inv-2026-05", date: "May 1, 2026", amount: "$49.00", status: "Paid" },
  { id: "inv-2026-04", date: "Apr 1, 2026", amount: "$49.00", status: "Paid" },
];

/* ── Notifications ───────────────────────────────────────────────────── */

export type NotificationEventId =
  | "new_lead"
  | "human_escalation"
  | "daily_digest"
  | "billing_alerts";

export interface NotificationEventConfig {
  id: NotificationEventId;
  label: string;
  description: string;
}

export const notificationEvents: NotificationEventConfig[] = [
  {
    id: "new_lead",
    label: "New lead captured",
    description: "A visitor shares contact details or asks to be followed up with.",
  },
  {
    id: "human_escalation",
    label: "Human escalation requested",
    description: "The AI hands off a conversation because it needs a person.",
  },
  {
    id: "daily_digest",
    label: "Daily summary digest",
    description: "A recap of conversations, leads, and orders from the last 24 hours.",
  },
  {
    id: "billing_alerts",
    label: "Billing alerts",
    description: "Payment failures, upcoming renewals, and usage-limit warnings.",
  },
];

export type NotificationChannel = "email" | "push";

export type NotificationPreferences = Record<
  NotificationEventId,
  Record<NotificationChannel, boolean>
>;

export const initialNotificationPreferences: NotificationPreferences = {
  new_lead: { email: true, push: true },
  human_escalation: { email: true, push: true },
  daily_digest: { email: true, push: false },
  billing_alerts: { email: true, push: false },
};

/* ── Developer / API & webhooks ──────────────────────────────────────── */

export const mockApiKey = "sk_live_51H8mQpKq9fT3zC7wYb3f2a";

export type WebhookEventId =
  | "message.received"
  | "conversation.escalated"
  | "order.created"
  | "lead.captured";

export interface WebhookEventOption {
  id: WebhookEventId;
  label: string;
}

export const webhookEventOptions: WebhookEventOption[] = [
  { id: "message.received", label: "message.received" },
  { id: "conversation.escalated", label: "conversation.escalated" },
  { id: "order.created", label: "order.created" },
  { id: "lead.captured", label: "lead.captured" },
];

export interface WebhookConfig {
  url: string;
  subscribedEvents: WebhookEventId[];
}

export const initialWebhookConfig: WebhookConfig = {
  url: "https://auralocloset.com/webhooks/noorbot",
  subscribedEvents: ["message.received", "order.created"],
};
